import { css } from '@emotion/react';
import React from 'react';
import { useSelector } from 'react-redux';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import NvIcon from 'shared/components/nv-icon';
import {
  gray2,
  gray6,
  gray5,
  highTide,
  white,
} from 'styles/global_defaults/colors';
import {
  textSmallFontSize,
} from 'styles/global_defaults/fonts';
import {
  doubleSpacing,
  extraLargeSpacing,
  halfSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { RootState } from 'redux/schemas';
import { getCourseAliases } from 'redux/selectors/course';
import {
  PERSONALIZED_TAKEAWAYS_SCROLL_EVENT,
  PersonalizedTakeawaysSectionId,
} from 'personalized_takeaways/components/personalized-takeaways-sections';
import { screenMdMax, screenSmMax } from 'styles/global_defaults/media-queries';

type PersonalizedTakeawaysSubmenuProps = {
  expanded: boolean;
};

const personalizedTakeawaysSectionIds = Object.values(PersonalizedTakeawaysSectionId);
const scrollSpyRootMargin = '-10% 0px -70% 0px';

const styles = css`
  .submenu-container {
    width: 240px;
    margin: 0 auto;
    padding-top: ${halfSpacing}px;

    .menu-separator {
      width: 100%;
      border-top: 1px solid ${gray6};
    }

    .back-row,
    .menu-item {
      width: 100%;
      height: ${extraLargeSpacing}px;
      border: none;
      color: ${gray2};
      cursor: pointer;
      display: flex;
      align-items: center;
      text-align: left;
      background: transparent;
      padding: ${halfSpacing}px ${standardSpacing}px;
      padding-left: ${doubleSpacing}px;

      > * + * {
        margin-left: ${standardSpacing}px;
      }
    }

    .back-icon {
      width: ${standardSpacing}px;
      height: ${standardSpacing}px;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .section-list {
      list-style: none;
      margin: 0;
      padding: ${halfSpacing}px 0 0;

      .section-index {
        width: ${standardSpacing}px;
        height: ${standardSpacing}px;
        border-radius: 50%;
        background: ${gray2};
        color: ${white};
        font-size: ${textSmallFontSize}px;
        font-weight: 400;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .menu-item.active .section-index {
        background: ${highTide};
      }
    }
  }

  &.collapsed {
    .submenu-container {
      width: 100%;

      .back-row,
      .menu-item {
        justify-content: center;
        padding-left: ${halfSpacing}px;
        padding-right: ${halfSpacing}px;
      }

      .back-label,
      .section-label {
        display: none;
      }
    }
  }
`;

const PersonalizedTakeawaysSubmenu = ({ expanded }: PersonalizedTakeawaysSubmenuProps) => {
  const { injectServices } = React.useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const catalogId = useSelector((state: RootState) => state.app.currentCatalogId);
  const aliases = useSelector((state: RootState) => getCourseAliases(state));
  const courseAliases = aliases?.courseAliases || {};
  const fromState = $state.params?.from;
  const [activeSection, setActiveSection] = React.useState(PersonalizedTakeawaysSectionId.AT_A_GLANCE);
  const [viewportWidth, setViewportWidth] = React.useState(window.innerWidth);
  const isMobile = viewportWidth <= screenSmMax;
  const isLargeDesktop = viewportWidth > screenMdMax;
  const isExpanded = isMobile || (expanded && isLargeDesktop);

  React.useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  React.useEffect(() => {
    const scrollContainer = document.getElementById('main-panel');
    const sectionElements = personalizedTakeawaysSectionIds
      .map((sectionId) => document.getElementById(sectionId))
      .filter((section): section is HTMLElement => section !== null);

    if (!scrollContainer || sectionElements.length === 0 || !window.IntersectionObserver) {
      return undefined;
    }

    const visibleSections = new Map<string, HTMLElement>();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visibleSections.set(entry.target.id, entry.target as HTMLElement);
        } else {
          visibleSections.delete(entry.target.id);
        }
      });

      const rootTop = scrollContainer.getBoundingClientRect().top;
      const activeSectionElement = Array.from(visibleSections.values()).sort((first, second) => (
        Math.abs(first.getBoundingClientRect().top - rootTop)
        - Math.abs(second.getBoundingClientRect().top - rootTop)
      ))[0];

      if (activeSectionElement) {
        setActiveSection(activeSectionElement.id as PersonalizedTakeawaysSectionId);
      }
    }, {
      root: scrollContainer,
      rootMargin: scrollSpyRootMargin,
      threshold: 0,
    });

    sectionElements.forEach((section) => observer.observe(section));

    return () => {
      observer.disconnect();
    };
  }, []);

  const sectionItems = [
    {
      id: PersonalizedTakeawaysSectionId.AT_A_GLANCE,
      label: t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.AT_A_GLANCE(),
    },
    {
      id: PersonalizedTakeawaysSectionId.IN_THE_NUMBERS,
      label: t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.IN_THE_NUMBERS(),
    },
    {
      id: PersonalizedTakeawaysSectionId.COURSE_HIGHLIGHTS,
      label: t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.COURSE_HIGHLIGHTS(courseAliases),
    },
    {
      id: PersonalizedTakeawaysSectionId.YOUR_STRENGTHS,
      label: t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOUR_STRENGTHS(),
    },
    {
      id: PersonalizedTakeawaysSectionId.YOUR_NEXT_LEVEL,
      label: t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOUR_NEXT_LEVEL(),
    },
    {
      id: PersonalizedTakeawaysSectionId.KEEP_LEARNING,
      label: t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.KEEP_LEARNING(),
    },
    {
      id: PersonalizedTakeawaysSectionId.CONGRATULATIONS,
      label: t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOU_DID_IT_LABEL(),
    },
  ];

  const onSectionClick = (sectionId: PersonalizedTakeawaysSectionId) => {
    setActiveSection(sectionId);

    window.dispatchEvent(new CustomEvent(PERSONALIZED_TAKEAWAYS_SCROLL_EVENT, {
      detail: {
        sectionId,
      },
    }));
  };

  const onBackClick = () => {
    if (!catalogId) {
      return;
    }

    if (fromState === 'learner-progress') {
      $state.go('learner-progress', { catalogId }, { replace: true });
      return;
    }

    $state.go('course-home', { catalogId });
  };

  return (
    <div css={styles} className={isExpanded ? 'expanded' : 'collapsed'}>
      <div className='submenu-container'>
        <div className='menu-separator' />
        <button
          type='button'
          className='back-row'
          onClick={onBackClick}
          aria-label={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.BACK_TO_COURSE(courseAliases)}
        >
          <span className='back-icon'>
            <NvIcon icon='back' size='small' color={gray5} />
          </span>
          <span className='back-label text-small semi-bold text-gray-2'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.BACK_TO_COURSE(courseAliases)}</span>
        </button>
        <div className='menu-separator' />

        <ul className='section-list'>
          {sectionItems.map((item, index) => (
            <li key={item.id}>
              <button
                type='button'
                className={`menu-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => onSectionClick(item.id)}
                aria-label={item.label}
                aria-current={activeSection === item.id ? 'page' : undefined}
              >
                <span className='section-index'>{index + 1}</span>
                <span className={`section-label text-small semi-bold ${activeSection === item.id ? 'text-primary' : 'text-gray-2'}`}>{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PersonalizedTakeawaysSubmenu;
